import React from "react";
import { WithFieldValue, DocumentData } from "firebase/firestore";
import { Alert, Text } from "@chakra-ui/react";

import { PRODUCTION_STEPS_AUX } from "@constants";
import { useCustomToast } from "@hooks";

import {
  JsonForm,
  JsonFormControl,
  makeCommentsControl,
  makeSelectControl,
} from "../JsonForm";
import { ButtonModal } from "../ModalWrappers";
import { useAddAuxStep } from "./useAddAuxStep";

type RemoveDebugTagModalProps = {
  itemId: string;
  debugTags: string[];
};

function makeTagControl(tags: string[]) {
  return [
    makeSelectControl(
      "Tag to remove",
      "tag",
      tags,
      "Please select an appropriate tag to remove.",
    ),
    makeCommentsControl(),
  ] as JsonFormControl[];
}

export function RemoveDebugTagModal({
  itemId,
  debugTags,
}: RemoveDebugTagModalProps) {
  const tagFormControl = React.useMemo(
    () => makeTagControl(debugTags),
    [debugTags],
  );
  const removeTagToast = useCustomToast(() => (
    <Alert status="success">Tag Added!</Alert>
  ));
  const [error, setError] = React.useState("");
  const addStep = useAddAuxStep(
    itemId,
    PRODUCTION_STEPS_AUX.STEP_AUX_REMOVE_DEBUG_TAG,
    removeTagToast,
    setError,
  );
  const doSubmit = React.useCallback(
    async function <T extends WithFieldValue<DocumentData>>(data: T) {
      const comments = data.comments ? (data.comments as string) : "";
      const tag = data.tag ? (data.tag as string) : "";
      if (tag) {
        addStep(comments, { tag });
      } else {
        setError("Select a tag to remove");
      }
    },
    [addStep],
  );

  return (
    <ButtonModal label="Remove Debug Tag" title="Remove Debug Tag" size="sm">
      <JsonForm fields={tagFormControl} onSubmit={doSubmit} clearAfterSubmit />
      {error ? <Text>{error}</Text> : undefined}
    </ButtonModal>
  );
}
